import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'react-hot-toast';
import { Search, Plus, Edit2, Trash2, Building2, MapPin, Phone, User, ChevronUp } from 'lucide-react';

export default function PurchasingDataPemesan() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  React.useEffect(() => { setCurrentPage(1); }, [search]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  
  const [form, setForm] = useState({
    nama_perusahaan: '',
    npwp: '',
    alamat: '',
    nama_pic: '',
    no_telp_pic: ''
  });

  const { data: pemesanList = [], isLoading } = useQuery({
    queryKey: ['data-pemesan'],
    queryFn: async () => {
      try {
        const res = await base44.entities.DataPemesan?.list();
        return res || [];
      } catch (err) {
        console.warn("Table DataPemesan might not exist yet", err);
        return [];
      }
    }
  });

  const createMut = useMutation({
    mutationFn: (data) => base44.entities.DataPemesan.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-pemesan'] });
      toast.success('Data Pemesan berhasil ditambahkan');
      setIsModalOpen(false);
    },
    onError: (err) => toast.error('Gagal menambah data: ' + err.message)
  });

  const updateMut = useMutation({
    mutationFn: ({ id, data }) => base44.entities.DataPemesan.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-pemesan'] });
      toast.success('Data Pemesan berhasil diupdate');
      setIsModalOpen(false);
    },
    onError: (err) => toast.error('Gagal update data: ' + err.message)
  });

  const deleteMut = useMutation({
    mutationFn: (id) => base44.entities.DataPemesan.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['data-pemesan'] });
      toast.success('Data Pemesan berhasil dihapus');
    },
    onError: (err) => toast.error('Gagal hapus data: ' + err.message)
  });

  const filtered = useMemo(() => {
    if (!search) return pemesanList;
    const q = search.toLowerCase();
    return pemesanList.filter(p => 
      (p.nama_perusahaan || '').toLowerCase().includes(q) ||
      (p.nama_pic || '').toLowerCase().includes(q)
    );
  }, [pemesanList, search]);

  const handleOpenAdd = () => {
    setEditingId(null);
    setForm({
      nama_perusahaan: '',
      npwp: '',
      alamat: '',
      nama_pic: '',
      no_telp_pic: ''
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (p) => {
    setEditingId(p.id);
    setForm({
      nama_perusahaan: p.nama_perusahaan || '',
      npwp: p.npwp || '',
      alamat: p.alamat || '',
      nama_pic: p.nama_pic || '',
      no_telp_pic: p.no_telp_pic || ''
    });
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    if (confirm('Yakin ingin menghapus data pemesan ini?')) {
      deleteMut.mutate(id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nama_perusahaan.trim()) return toast.error('Nama Perusahaan wajib diisi');
    
    if (editingId) {
      updateMut.mutate({ id: editingId, data: form });
    } else {
      createMut.mutate(form);
    }
  };

  return (
    <div className="space-y-6 text-[#302621]">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight text-slate-800">Data Pemesan (Kop PO)</h1>
          <p className="text-xs text-slate-500 mt-1">
            Kelola profil perusahaan yang digunakan untuk pemesanan (tercetak di PDF Purchase Order).
          </p>
        </div>
        <Button 
          onClick={handleOpenAdd}
          className="bg-[#8C6208] text-white hover:bg-[#724F06] gap-2 text-xs font-bold rounded-xl h-10 shadow-sm"
        >
          <Plus className="w-4 h-4" /> Tambah Data Pemesan
        </Button>
      </div>

      <Card className="border-[#E6D5B8]/40 shadow-sm bg-white rounded-2xl overflow-hidden">
        <div className="p-4 bg-slate-50 border-b border-slate-100">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Cari nama perusahaan, PIC..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-9 h-9 text-xs"
            />
          </div>
        </div>

        <div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {isLoading ? (
            <div className="col-span-full py-12 text-center text-sm text-slate-400">Memuat data pemesan...</div>
          ) : filtered.length === 0 ? (
            <div className="col-span-full py-12 text-center text-sm text-slate-400">Belum ada data pemesan.</div>
          ) : (
            filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map(p => (
              <Card key={p.id} className="border border-slate-100 shadow-xs hover:shadow-md transition-shadow">
                <CardContent className="p-4 relative group">
                  <div className="absolute top-4 right-4 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-blue-600" onClick={() => handleOpenEdit(p)}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-rose-600" onClick={() => handleDelete(p.id)}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                  
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center shrink-0 border border-blue-100">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="pr-10">
                      <h3 className="font-bold text-sm text-slate-800">{p.nama_perusahaan}</h3>
                      {p.npwp && (
                        <p className="text-[10px] text-slate-500 mt-0.5 font-mono">NPWP: {p.npwp}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
                    <div className="flex items-start gap-2 text-xs">
                      <User className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">Nama PIC</p>
                        <p className="font-medium text-slate-700 truncate">{p.nama_pic || '-'}</p>
                      </div>
                    </div>
                    {p.no_telp_pic && (
                      <div className="flex items-start gap-2 text-xs">
                        <Phone className="w-3.5 h-3.5 text-slate-400 mt-0.5" />
                        <div>
                          <p className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">No. Telp PIC</p>
                          <p className="font-medium text-slate-700 truncate">{p.no_telp_pic}</p>
                        </div>
                      </div>
                    )}
                    {p.alamat && (
                      <div className="flex items-start gap-2 text-xs">
                        <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                        <div>
                          <p className="text-slate-500 text-[10px] uppercase font-bold tracking-wider">Alamat Pengiriman</p>
                          <p className="font-medium text-slate-700 text-[10px] leading-tight line-clamp-2">{p.alamat}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
          
          {/* ── Pagination Controls ── */}
          {filtered.length > 0 && (
            <div className="col-span-full px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
                <div className="w-[80px]">
                  <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                    <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 25, 50, 100].map(n => (
                        <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &lt;
                </button>
                
                {(() => {
                  const totalPages = Math.ceil(filtered.length / itemsPerPage);
                  const pages = [];
                  for(let i=1; i<=totalPages; i++) {
                    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                            currentPage === i 
                              ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                              : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    } else if (i === currentPage - 2 || i === currentPage + 2) {
                      pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                    }
                  }
                  return pages;
                })()}
                
                <button 
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(filtered.length / itemsPerPage), p + 1))}
                  disabled={currentPage >= Math.ceil(filtered.length / itemsPerPage)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit Data Pemesan' : 'Tambah Data Pemesan'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 text-xs mt-2">
            <div className="space-y-1.5">
              <Label className="font-bold uppercase text-[10px] text-slate-500">Nama Perusahaan Kita <span className="text-rose-500">*</span></Label>
              <Input 
                value={form.nama_perusahaan} 
                onChange={e => setForm({...form, nama_perusahaan: e.target.value})} 
                placeholder="Contoh: PT. Satala Dermatech"
                autoFocus
                required
              />
            </div>
            
            <div className="space-y-1.5">
              <Label className="font-bold uppercase text-[10px] text-slate-500">NPWP Perusahaan</Label>
              <Input 
                value={form.npwp} 
                onChange={e => setForm({...form, npwp: e.target.value})} 
                placeholder="Contoh: 01.234.567.8-012.000"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="font-bold uppercase text-[10px] text-slate-500">Nama PIC Kita</Label>
                <Input 
                  value={form.nama_pic} 
                  onChange={e => setForm({...form, nama_pic: e.target.value})} 
                  placeholder="Contoh: Developer Satala"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold uppercase text-[10px] text-slate-500">No. Telp PIC</Label>
                <Input 
                  value={form.no_telp_pic} 
                  onChange={e => setForm({...form, no_telp_pic: e.target.value})} 
                  placeholder="Contoh: 08123456789"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="font-bold uppercase text-[10px] text-slate-500">Alamat Pengiriman Lengkap</Label>
              <Input 
                value={form.alamat} 
                onChange={e => setForm({...form, alamat: e.target.value})} 
                placeholder="Jl. Raya Solo-Tawangmangu No. 123..."
              />
            </div>

            <div className="pt-4 flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)}>Batal</Button>
              <Button type="submit" className="bg-[#8C6208] text-white hover:bg-[#724F06]">
                Simpan Data
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
